// Helper function to check if a date is a holiday
const rv74isHoliday = (date, holidaysSpecificYear, holidaysAllYears) => {
  const dateString = date.toISOString().split('T')[0];
  const yearSpecificHolidays = holidaysSpecificYear.includes(dateString, holidaysSpecificYear, holidaysAllYears);
  const allYearHolidays = holidaysAllYears.some(holiday => {
    const [month, day] = holiday.split('-').map(Number);
    return date.getMonth() + 1 === month && date.getDate() === day;
  });
  return yearSpecificHolidays || allYearHolidays;
};

// Function to add business days to a date, excluding weekends and holidays
const rv74addBusinessDays = (startDate, days, holidaysSpecificYear, holidaysAllYears) => {
  let date = new Date(startDate);
  let addedDays = 0;

  while (addedDays < days) {
    date.setDate(date.getDate() + 1);
    const dayOfWeek = date.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6 && !rv74isHoliday(date, holidaysSpecificYear, holidaysAllYears)) {
      addedDays++;
    }
  }

  return date;
};

function rv74FormatDate(date) {
  const day = String(date.getDate()).padStart(2, '0'); // Ensure day is two digits
  const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() is zero-based
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
}

function rv74ApplyDeliveryTemplate(template, minValue, maxValue, timeUnit) {
  let text = template || '';
  if (timeUnit) {
    text = text.replace(/\b(weeks|semanas|semaines|wochen|settimane)\b/gi, '[time_unit]');
    text = text.split('[time_unit]').join(timeUnit);
  }
  text = text.replace('[min]', minValue);
  if (maxValue != null && maxValue !== '') {
    text = text.replace('[max]', maxValue);
  }
  return text;
}

class DeliveryDateCartComponent extends HTMLElement {
  connectedCallback() {
    
    this.checkIfCartNeedsUpdates();

    const minDateStr = this.dataset.dateMin;
    const maxDateStr = this.dataset.dateMax;

    if (!minDateStr) return;

    const mode = this.dataset.visualizationMode;
    const textTemplate = maxDateStr ? this.dataset.visualizationText : this.dataset.visualizationTextSingle;
    const shopLocale = Shopify.locale || 'en-US';  // Default to 'en-US' if Shopify.locale is unavailable
    this.message = this.querySelector('[data-delivery-message]')

    // Convert date strings to Date objects
    const minDate = new Date(minDateStr);
    const maxDate = maxDateStr ? new Date(maxDateStr) : null;

    // Helper function to calculate weeks based on days
    const calculateWeeks = (days) => Math.ceil(days / 5);

    // Render delivery date based on the selected mode
    let displayText = textTemplate;

    if (mode === "Días") {
      const minDays = Math.floor((minDate - new Date()) / (1000 * 60 * 60 * 24));
      const maxDays = maxDate ? Math.floor((maxDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
      displayText = rv74ApplyDeliveryTemplate(
        displayText,
        minDays,
        maxDays,
        this.dataset.textDays || 'days'
      );

    } else if (mode === "Semanas") {
      const minDays = Math.floor((minDate - new Date()) / (1000 * 60 * 60 * 24));
      const maxDays = maxDate ? Math.floor((maxDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
      let minValue;
      let maxValue = null;
      let timeUnit;

      if (minDays >= 5) {
        timeUnit = this.dataset.textWeeks || 'weeks';
        minValue = calculateWeeks(minDays);
        maxValue = maxDays != null ? calculateWeeks(maxDays) : null;
      } else {
        timeUnit = this.dataset.textDays || 'days';
        minValue = minDays;
        maxValue = maxDays;
      }

      displayText = rv74ApplyDeliveryTemplate(displayText, minValue, maxValue, timeUnit);

    } else if (mode === "Fechas") {
      // Format dates according to the locale
      const formatDate = (date, options) => date.toLocaleDateString(shopLocale, options);

      if (maxDate) {
        // Determine if the month is the same for min and max dates
        const minMonth = minDate.getMonth();
        const maxMonth = maxDate.getMonth();
        const minDateFormatted = formatDate(minDate, {
          day: 'numeric',
          month: minMonth === maxMonth ? undefined : 'short'  // Only show month if different from max date
        });
        const maxDateFormatted = formatDate(maxDate, {
          day: 'numeric',
          month: 'short'
        });

        displayText = displayText.replace('[min]', minDateFormatted).replace('[max]', maxDateFormatted);
      } else {
        const minDateFormatted = formatDate(minDate, {
          day: 'numeric',
          month: 'short'
        });
        displayText = displayText.replace('[min]', minDateFormatted);
      }
    }
    
    this.message.innerHTML = `
      <span>${displayText}</span>
      <input type="hidden" name="attributes[_min_delivery_date]" value="${minDate.toISOString().split('T')[0]}">
      ${maxDate ? `<input type="hidden" name="attributes[_max_delivery_date]" value="${maxDate.toISOString().split('T')[0]}">` : ''}
    `;
  }

  async checkIfCartNeedsUpdates() {
    let cartItems = await this.getCartItems();
    const cartItemsChanged = await this.maybeMergeCartItems(cartItems);
    if (cartItemsChanged) {
      cartItems = await this.getCartItems();
    }
    await this.maybeUpdateCartItemDates(cartItems);
    document.dispatchEvent(new CustomEvent('delivery-dates:reload'));
  }

  async getCartItems() {
    const cartItems = await fetch(`${window.Shopify.routes.root}?section_id=cart-extended`)
      .then(response => response.text())
      .then(async section => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(section, 'text/html');
        const cartItemsData = doc.getElementById('cart-items-data').textContent;
        const cartItemsJson = JSON.parse(cartItemsData);
        return cartItemsJson;
      })

    return cartItems;
  }

  async maybeMergeCartItems(cartItems) {
    // Group items by variant_id
    const groupedItems = cartItems.reduce((acc, item) => {
      const key = item.variant_id;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});

    // Filter out groups with only 1 item
    const multipleItemGroups = Object.fromEntries(
      Object.entries(groupedItems).filter(([_, items]) => items.length > 1)
    );
    
    if (!Object.keys(multipleItemGroups).length) {
      return false;
    }

    const loader = document.getElementById('cart-drawer-loader');
    loader.classList.remove('cart-loader--hidden');

    try {
      for (const items of Object.values(multipleItemGroups)) {
        if (items.length <= 1) continue;

        // Check if items differ only by delivery dates
        const itemsWithSameProps = items.every(item => {
          const baseItem = items[0];
          const itemProps = {...item.properties};
          const baseProps = {...baseItem.properties};
          
          delete itemProps._min_delivery_date;
          delete itemProps._max_delivery_date;
          delete baseProps._min_delivery_date;
          delete baseProps._max_delivery_date;
          
          return JSON.stringify(itemProps) === JSON.stringify(baseProps);
        });

        if (!itemsWithSameProps) continue;

        // Calculate total quantity and latest delivery dates
        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        const latestMinDate = items.reduce((latest, item) => {
          const currentDate = item.properties._min_delivery_date;
          return !latest || (currentDate && currentDate > latest) ? currentDate : latest;
        }, null);
        const latestMaxDate = items.reduce((latest, item) => {
          const currentDate = item.properties._max_delivery_date;
          return !latest || (currentDate && currentDate > latest) ? currentDate : latest;
        }, null);

        // First remove all items except the one we want to keep
        const updates = {};
        items.slice(1).forEach(item => {
          updates[item.key] = 0;
        });

        // Wait for the first update
        const updatedCart = await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ updates })
        }).then(response => response.json());

        // Create the update object for the first item
        const firstItemUpdate = {
          id: items[0].key,
          quantity: totalQuantity,
          properties: {
            ...items[0].properties,
            _min_delivery_date: latestMinDate,
            _max_delivery_date: latestMaxDate
          }
        };

        // Wait for the second update
        await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(firstItemUpdate)
        }).then(response => response.json());
      }

      document.dispatchEvent(new CustomEvent('cart:build'));
      loader.classList.add('cart-loader--hidden');
      return true;

    } catch (error) {
      console.error('Error updating cart:', error);
      loader.classList.add('cart-loader--hidden');
      return false;
    }
  }

  async maybeUpdateCartItemDates(cartItems) {
    // This are the default min and max delivery days for the order
    const minDeliveryDaysDefault = this.dataset.minDefaultDeliveryDays;
    const maxDeliveryDaysDefault = this.dataset.maxDefaultDeliveryDays;

    for (const item of cartItems) {
      const currentMinDate = item.properties._min_delivery_date;
      const currentMaxDate = item.properties._max_delivery_date;

      let lineMinDays = item.delivery_days_min ? item.delivery_days_min : minDeliveryDaysDefault;
      let lineMaxDays = item.delivery_days_max ? item.delivery_days_max : maxDeliveryDaysDefault;

      const quantity = item.quantity;
      const qtyAvailable = item.inventory_quantity;

      if (quantity > qtyAvailable) {
        const providerMinDays = item.supplier_days_min ? item.supplier_days_min : 0;
        const providerMaxDays = item.supplier_days_max ? item.supplier_days_max : 0;

        if (this.dataset.addDeliveryTimeToSupplierTime === 'true') {
          lineMinDays = lineMinDays + providerMinDays;
          lineMaxDays = lineMaxDays + providerMaxDays;
        } else {
          lineMinDays = providerMinDays;
          lineMaxDays = providerMaxDays;
        }

      }

      const holidaysSpecificYear = this.dataset.holidaysSpecificYear.split(',');
      const holidaysAllYears = this.dataset.holidaysAllYears.split(',');

      // Calculate the specific min and max delivery dates in all cases
      const startDate = new Date();  // Calculated from moment order is placed
      let minDate, maxDate;

      if (lineMinDays > 0) {
        minDate = rv74addBusinessDays(startDate, lineMinDays, holidaysSpecificYear, holidaysAllYears);
      } else {
        minDate = new Date(startDate);
      }

      if (lineMaxDays > 0) {
        maxDate = rv74addBusinessDays(startDate, lineMaxDays, holidaysSpecificYear, holidaysAllYears);
      } else {
        maxDate = new Date(startDate);
      }

      const minDateFormatted = rv74FormatDate(minDate);
      const maxDateFormatted = rv74FormatDate(maxDate);

      if (minDateFormatted !== currentMinDate || maxDateFormatted !== currentMaxDate) {

        const loader = document.getElementById('cart-drawer-loader');
        loader.classList.remove('cart-loader--hidden');

        const data = {
          id: item.key,
          quantity: item.quantity,
          properties: {
            _min_delivery_date: minDateFormatted,
            _max_delivery_date: maxDateFormatted
          }
        };

        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(data)
          });

          document.dispatchEvent(new CustomEvent('cart:build'));
          loader.classList.add('cart-loader--hidden');
        } catch (error) {
          console.error('Error updating cart:', error);
        }
      }
    }
  }
}
customElements.define('delivery-date-cart-component', DeliveryDateCartComponent);

class DeliveryDateComponent extends HTMLElement {
  constructor() {
    super();
    // Bind the method once in constructor
    this.boundReload = this.reload.bind(this);
    this._instanceId = Math.random();
  }

  connectedCallback() {
    if (this.isInitialized) return;
    this.isInitialized = true;
    this.init();   
  }

  init() {
    this.formId = this.dataset.formId;
    this.message = this.querySelector('[data-delivery-message]');
    
    // Use the bound method we created in constructor
    document.addEventListener('delivery-dates:reload', this.boundReload);
    
    const minDays = parseInt(this.dataset.totalMin, 10);
    const maxDays = parseInt(this.dataset.totalMax, 10);
    const mode = this.dataset.visualizationMode;
    const textTemplate = maxDays ? this.dataset.visualizationText : this.dataset.visualizationTextSingle;
    const holidaysSpecificYear = this.dataset.holidaysSpecificYear.split(',');
    const holidaysAllYears = this.dataset.holidaysAllYears.split(',');

    const shopLocale = Shopify.locale || 'en-US';  // Default to 'en-US' if Shopify.locale is unavailable

    // Calculate the specific min and max delivery dates in all cases
    const startDate = new Date();  // Assuming calculation starts from today
    const minDate = rv74addBusinessDays(startDate, minDays, holidaysSpecificYear, holidaysAllYears);
    const maxDate = maxDays ? rv74addBusinessDays(startDate, maxDays, holidaysSpecificYear, holidaysAllYears) : null;

    // Convert total days to weeks for "Semanas" mode
    const calculateWeeks = (days) => Math.ceil(days / 5);

    // Render delivery date based on the selected mode
    let displayText = textTemplate;
    if (mode === "Días") {
      displayText = rv74ApplyDeliveryTemplate(
        displayText,
        minDays,
        maxDays || null,
        this.dataset.textDays
      );
    } else if (mode === "Semanas") {
      let minValue, maxValue, timeUnit;
      if (minDays >= 5) {
        timeUnit = this.dataset.textWeeks;
        minValue = calculateWeeks(minDays);
        maxValue = maxDays ? calculateWeeks(maxDays) : null;
      } else {
        timeUnit = this.dataset.textDays;
        minValue = minDays;
        maxValue = maxDays ? maxDays : null;
      }

      displayText = rv74ApplyDeliveryTemplate(displayText, minValue, maxValue, timeUnit);

    } else if (mode === "Fechas") {
      // Format dates according to the locale
      const formatDate = (date, options) => date.toLocaleDateString(shopLocale, options);

      if (maxDate) {
        // Determine if the month is the same for min and max dates
        const minMonth = minDate.getMonth();
        const maxMonth = maxDate.getMonth();
        const minDateFormatted = formatDate(minDate, {
          day: 'numeric',
          month: minMonth === maxMonth ? undefined : 'short'  // Only show month if different from max date
        });
        const maxDateFormatted = formatDate(maxDate, {
          day: 'numeric',
          month: 'short'
        });

        // Combine formatted dates
        displayText = displayText.replace('[min]', minDateFormatted).replace('[max]', maxDateFormatted);
      } else {
        const minDateFormatted = formatDate(minDate, {
          day: 'numeric',
          month: 'short'
        });
        displayText = displayText.replace('[min]', minDateFormatted);
      }
    }

    // Render output in the component, including hidden inputs for min and max delivery dates
    const hiddenInputs = maxDate ? `
      <input type="hidden" form="${this.formId}" name="properties[_min_delivery_date]" value="${minDate.toISOString().split('T')[0]}">
      <input type="hidden" form="${this.formId}" name="properties[_max_delivery_date]" value="${maxDate.toISOString().split('T')[0]}">
    ` : `
      <input type="hidden" form="${this.formId}" name="properties[_min_delivery_date]" value="${minDate.toISOString().split('T')[0]}">
    `;

    this.message.innerHTML = `
      <span>${displayText}</span>
      ${hiddenInputs}
    `;
  }

  disconnectedCallback() {
    // Remove the event listener using the same bound function reference
    document.removeEventListener('delivery-dates:reload', this.boundReload);
  }

  async reload() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}products/${this.dataset.productHandle}?section_id=delivery-dates`);
      const section = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(section, 'text/html');
      
      const currentDeliveryDateComponent = document.getElementById('deliveryDateComponent');
      const newDeliveryDateComponent = doc.getElementById('deliveryDateComponent');
      
      // Replace the entire component instead of just its innerHTML
      if (currentDeliveryDateComponent && newDeliveryDateComponent) {
        currentDeliveryDateComponent.replaceWith(newDeliveryDateComponent);
      }
    } catch (error) {
      console.error('Error reloading delivery dates:', error);
    }
  }
}

customElements.define('delivery-date-component', DeliveryDateComponent);